/**
 * Source Drawer System (A.11) — UI-level types.
 *
 * The domain `SourceType` in `@ando/core/domain/source` is intentionally
 * coarse (six buckets: document / email / chat / database / web / other).
 * Viewers, however, need a granular discriminator: a PDF and a XLSX both
 * live under `document` but render with entirely different chrome. We
 * therefore introduce a UI-only `SourceViewerType` enumerating the
 * granular renderers shipped under `viewers/...`.
 *
 * Adding a new viewer = new entry in this enum + matching `viewers/<x>/`
 * folder. The shell (`SourceDrawer.tsx`) is agnostic — it only routes the
 * `kind` to the registered viewer / actions plugin.
 */
import type { ReactNode } from "react";

/**
 * Coarse domain type — duplicated locally as a string-literal union so
 * `@ando/ui` stays a *leaf* package (must not depend on `@ando/core`).
 * Keep in sync with `packages/core/src/domain/source.ts`.
 *
 * Identical contract as `MessageCitationSourceType` in `MessageCitation.tsx`;
 * we re-declare it under a more descriptive name to keep this file's
 * grammar tight (a citation has a *source type*, the drawer routes by it).
 */
export type SourceDomainType =
  | "document"
  | "email"
  | "chat"
  | "database"
  | "web"
  | "other";

/** Granular UI viewer kinds — superset of `SourceDomainType` for rendering. */
export type SourceViewerType =
  | "pdf"
  | "email"
  | "calendar_event"
  | "db_row"
  | "banking_transaction"
  | "web";

/**
 * Citation anchor — *where* in the rendered source the citation points.
 *
 * Optional fields are viewer-specific (e.g. `page` for PDF, `cell` for
 * XLSX, `row_id` for DB). Viewers ignore irrelevant keys; the universal
 * `CitationHighlighter` reads `text` to draw an inline `<mark>` overlay
 * when nothing more precise is available.
 */
export interface CitationAnchor {
  /** PDF page (1-based). */
  page?: number;
  /** Bounding box on the page (PDF / image), `[x, y, w, h]` in % of viewport. */
  bbox?: [number, number, number, number];
  /** XLSX cell address, e.g. "B12". */
  cell?: string;
  /** Email block id (header / body / attachment). */
  blockId?: string;
  /** DB row identifier (primary key or hash). */
  rowId?: string;
  /** Free-form text fragment to highlight inside the viewer. */
  text?: string;
}

/**
 * Citation context attached to the open drawer — the chat-side snippet
 * that triggered the source open, plus the anchor we want to focus on.
 *
 * Rendered in the Drawer's "Citation context" zone; clicking the snippet
 * scrolls + pulses the highlighted zone inside the viewer.
 */
export interface CitationContext {
  /** 1-based citation index as it appeared in chat (e.g. `[2]`). */
  index: number;
  /** Verbatim chat excerpt that produced the citation. */
  excerpt: string;
  /** Where in the source the citation lands. */
  anchor: CitationAnchor;
  /** ISO timestamp of when the citation was emitted in chat. */
  citedAt?: string;
}

/** Breadcrumb shown in the Drawer footer. */
export interface SourceOrigin {
  /** Connector / vendor label, e.g. `vendor@alpha.it`. */
  connectorLabel: string;
  /**
   * Breadcrumb segments — rendered joined by " · ".
   *
   * Wire shape from ``GET /api/v1/sources/{id}/resolve`` (BE serializer in
   * ``ando-agent/src/services/sources/...``) returns an array of segments
   * (e.g. ``["Email", "Conversazioni"]``). A plain string is also
   * accepted for callers that built the breadcrumb client-side; the
   * renderer normalises both via :func:`formatBreadcrumb`.
   */
  breadcrumb: readonly string[] | string;
  /** Optional deep-link to the original context (full thread / full table). */
  href?: string;
}

/** Toolbar action descriptor — schema-driven per viewer. */
export interface SourceAction {
  id: string;
  label: string;
  icon?: ReactNode;
  /** `primary` = filled accent, `default` = ghost, `destructive` = red. */
  variant?: "primary" | "default" | "destructive";
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * Universal source descriptor consumed by the Drawer shell.
 *
 * The `payload` is viewer-specific and typed `unknown` here — each viewer
 * narrows its own shape via a Zod-or-cast guard. We deliberately don't
 * pull every viewer payload into this union to keep the shell tree-shake
 * friendly (apps can lazy-load a viewer without dragging the whole
 * dependency graph).
 */
export interface SourceMeta {
  /** Stable id (UUID) — usually mirrors `Citation.id` from the message. */
  id: string;
  /** Granular renderer kind. */
  kind: SourceViewerType;
  /** Coarse domain type — kept for downstream analytics + routing. */
  domainType: SourceDomainType;
  /** Display title (filename, table name, subject…). */
  title: string;
  /**
   * Right-side header meta (size, date, modified-by…).
   *
   * Wire shape from ``GET /api/v1/sources/{id}/resolve`` is a dict of
   * per-kind fields (page count for PDFs, sender + received_at for
   * emails, etc.). Legacy fixtures / client-side builds may pass a
   * preformatted string. The renderer reads both via
   * :func:`formatSourceMetaSubtitle`.
   */
  meta?: Record<string, unknown> | string;
  /** Origin breadcrumb shown in the footer. */
  origin: SourceOrigin;
  /** Viewer-specific payload — see each viewer's `*Payload` type. */
  payload: unknown;
  /** Optional citation context (where we landed from). */
  citation?: CitationContext;
}

/**
 * Inline metadata the chat already has about a citation — passed
 * alongside the click so the resolver can short-circuit the BE
 * round-trip when it already knows the destination (e.g. ``web``
 * sources carry the full URL on the wire). Jun 2026.
 */
export interface CitationHints {
  /** External URL — populated for ``web`` source-type citations. */
  readonly url?: string;
  /** Human-readable title from the source. */
  readonly title?: string;
  /** Short text snippet excerpted from the source. */
  readonly snippet?: string;
  /** Optional published date (ISO 8601). */
  readonly publishedDate?: string;
}

/** Citation-click contract emitted by `<MessageCitation>` / `<MessageBody>`. */
export type CitationClickHandler = (
  sourceId: string,
  sourceType?: SourceDomainType,
  hints?: CitationHints,
) => void;

/**
 * Normalise the breadcrumb shape into a human " · "-joined string for
 * inline rendering in the Drawer footer.
 *
 * The BE wire shape is ``string[]``; legacy callers pass a single
 * preformatted ``string``. Both produce the same output here. Empty
 * arrays / strings collapse to ``""`` so the caller can hide the slot.
 */
export function formatBreadcrumb(
  value: SourceOrigin["breadcrumb"] | undefined,
): string {
  if (!value) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(" · ");
  // narrowed: branch is `string` (Array.isArray excluded the readonly tuple).
  return value as string;
}

/**
 * Render the optional meta dict / string as a compact subtitle line.
 *
 * Dict entries are joined as ``"key: value · key: value"`` so the chip
 * stays readable in the header without committing to a per-kind layout.
 * Values that are objects/arrays are stringified via ``JSON.stringify``
 * (defensive — most BE-emitted ``meta`` keys are scalars).
 */
export function formatSourceMetaSubtitle(
  value: SourceMeta["meta"] | undefined,
): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const parts: string[] = [];
  for (const [key, raw] of Object.entries(value)) {
    if (raw === null || raw === undefined) continue;
    const rendered =
      typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean"
        ? String(raw)
        : JSON.stringify(raw);
    parts.push(`${key}: ${rendered}`);
  }
  return parts.join(" · ");
}
